import React, { useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadTaskProgress } from 'actions/profile';
import { Copy, Forms, Modal, Help, Loading, ArrayHelpers, SystemText } from 'e4shared';
import BlobImage from 'components/blobImage';
import Rating from 'components/general/rating';
import FileLink from 'components/general/fileLink';
import TaskIcon from '../../../task/components/taskIcon';
import { e4Format, isPast, isToday } from 'helpers/dates';
import './progress.css';

export const Progress = ({ close, history, task, show }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(null);

    const progress = useSelector(state => state?.profile?.taskProgress);

    const gotoTask = (profileId, profileTaskId) => {
        setOpen(null);
        close();
        history.push(`/objectives/${profileId}/objective/${profileTaskId}`);
    };

    const closeAndClean = () => {
        setOpen(null);
        close();
    };

    const setOpenHandler = i => {
        open !== i ? setOpen(i) : setOpen(null);
    };

    useEffect(() => {
        if (task && task?.task_Id) {
            dispatch(loadTaskProgress(task.task_Id));
        }
    }, [dispatch, task, task?.task_Id]);

    if (!task) return null;

    const footer = (
        <div>
            <button data-testid="button-close" className="btn" onClick={() => closeAndClean()}>
                {SystemText('ObjectiveTracker/Progress/Buttons/Close')}
            </button>
        </div>
    );

    const ratingHelp = task => {
        if (ArrayHelpers.existsAndHasLength(task?.competencies) && task.competencies.filter(c => c.rating > 0).length > 0) {
            return (
                <Help id={`rating-task${task.id}`}>
                    <span>
                        <b>{SystemText('ObjectiveTracker/Progress/Help/CompetencyScorecard')}</b>
                        {task.competencies.map((c, i) => {
                            return (
                                <span key={`breakdown-${i}`}>
                                    <br />
                                    {`${c.area} - ${c.name} - ${c.rating}`}
                                </span>
                            );
                        })}
                    </span>
                </Help>
            );
        } else {
            return null;
        }
    };

    const rating = task => {
        let res = 0;
        if (ArrayHelpers.existsAndHasLength(task?.competencies)) {
            const avg = task.competencies.reduce((a, b) => a + b.rating, 0) / task.competencies.length;
            const avgFixed = avg % 1 === 0 ? avg : avg.toFixed(1);
            res = avgFixed;
        } else {
            res = task?.rating;
        }

        return (
            <span className="inline-rating">
                <Rating hideLabel smallStar value={res} />
            </span>
        );
    };
    const getStatusAndLabel = user => {
        let status = '';
        let label = '';

        if (user?.date_Completed) {
            status =
                task?.requires_Manager && !user?.approved
                    ? SystemText('ObjectiveTracker/Progress/Status/AwaitingApproval')
                    : SystemText('ObjectiveTracker/Progress/Status/Complete');
        } else {
            status =
                isPast(task?.due) && !isToday(task?.due)
                    ? SystemText('ObjectiveTracker/Progress/Status/Overdue')
                    : SystemText('ObjectiveTracker/Progress/Status/Incomplete');
        }

        switch (status) {
            case SystemText('ObjectiveTracker/Progress/Status/AwaitingApproval'):
                label = 'warning';
                break;
            case SystemText('ObjectiveTracker/Progress/Status/Incomplete'):
                label = 'info';
                break;
            case SystemText('ObjectiveTracker/Progress/Status/Overdue'):
                label = 'danger';
                break;
            default:
                label = 'success';
        }
        return { status, label };
    };

    const getColourAndBar = progress => {
        let colour = 'grey';
        let bar = 'disabled';
        if (!progress) {
            colour = 'red';
            bar = 'danger';
        } else if (progress === 100) {
            colour = 'green';
            bar = 'success';
        } else {
            colour = 'orange';
            bar = 'warning';
        }

        return { colour, bar };
    };

    return (
        <Modal show={show} title={SystemText('ObjectiveTracker/Progress/Modal/Title')} close={closeAndClean} footer={footer}>
            <div className="task-progress row">
                <div className="col-md-12">
                    <Forms.Label
                        label={SystemText('ObjectiveTracker/Progress/Labels/Objective')}
                        value={task.name}
                        suffix={() => <TaskIcon icons={task?.icons} />}
                    />
                    <Forms.Label label={SystemText('ObjectiveTracker/Progress/Labels/Due')} value={e4Format(task?.due)} />
                    <div className="form-group">
                        <label>{SystemText('ObjectiveTracker/Progress/Labels/Description')}</label>
                        <div className="mb10">
                            <Copy value={task?.description} />
                        </div>
                    </div>
                </div>
                <div className="col-md-12 form-group">
                    <label>{SystemText('ObjectiveTracker/Progress/Labels/Progress')}</label>
                    {!progress && <Loading />}
                    {progress?.map((user, i) => {
                        const { status, label } = getStatusAndLabel(user);

                        return (
                            <div key={`${user?.profile_Id}_${i}`} className="row hover">
                                <div className="summary pointer" data-testid="div-opener" onClick={() => setOpenHandler(i)}>
                                    {open === i ? (
                                        <i className="mr10 fa fa-minus-square" />
                                    ) : (
                                        <i className="mr10 fa fa-plus-square" />
                                    )}
                                    <span className="mr10">
                                        <Link to={`/search/${user?.full_Name}`}>
                                            <BlobImage
                                                height="30"
                                                className="img-circle mr10"
                                                folder="avatar"
                                                initials={user?.initials}
                                                src={user?.avatar}
                                            />
                                        </Link>
                                        {user?.full_Name}
                                    </span>
                                    <span className={`label label-${label}`}>{status}</span>
                                    <span className="ml5">
                                        <button
                                            className="btn btn-xs btn-link"
                                            onClick={() => gotoTask(user?.profile_Id, user?.profile_Task_Id)}
                                        >
                                            {SystemText('ObjectiveTracker/Progress/Buttons/View')}
                                        </button>
                                    </span>
                                </div>
                                {open === i && (
                                    <div className="details">
                                        <label>{SystemText('ObjectiveTracker/Progress/Labels/Completed')}</label>
                                        <div>{user?.date_Completed ? e4Format(user.date_Completed) : '-'}</div>
                                        {
                                            <>
                                                <label>{SystemText('ObjectiveTracker/Progress/Labels/Comments')}</label>
                                                <div>{user?.comments ? <Copy value={user?.comments} /> : '-'}</div>
                                                {user?.task_Completion_File && (
                                                    <FileLink folder="task_Completion" file={user.task_Completion_File} />
                                                )}
                                                {task?.requires_Manager && user?.date_Completed && (
                                                    <div>
                                                        <label>
                                                            {SystemText('ObjectiveTracker/Progress/Labels/ApprovedBy')}
                                                        </label>
                                                        <div>
                                                            {user?.feedback_From ? (
                                                                <div>
                                                                    <BlobImage
                                                                        height="30"
                                                                        className="img-circle mr10"
                                                                        folder="avatar"
                                                                        initials={user.feedback_Initials}
                                                                        src={user.feedback_Avatar}
                                                                    />
                                                                    <Link to={`/search/${user?.feedback_Full_Name}`}>
                                                                        {user?.feedback_Full_Name}
                                                                    </Link>
                                                                </div>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </div>
                                                        <label>
                                                            {SystemText('ObjectiveTracker/Progress/Labels/Feedback')}
                                                        </label>
                                                        {!user?.feedback_1 && !user?.feedback_2 && !user?.feedback_3 && (
                                                            <div>-</div>
                                                        )}
                                                        <div>{user?.feedback_1 ? <Copy value={user.feedback_1} /> : ''}</div>
                                                        <div>{user?.feedback_2 ? <Copy value={user.feedback_2} /> : ''}</div>
                                                        <div>{user.feedback_3 ? <Copy value={user.feedback_3} /> : ''}</div>
                                                        <div>
                                                            <span className="flex">
                                                                {rating(user)}
                                                                {ratingHelp(user)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        }
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};

export default withRouter(Progress);
